import {
	AddressType,
	BrandFileType,
	useDeleteBrandMutation,
	useGenerateOrganizationLogoUploadUrlMutation,
	useGetAllBrandsQuery,
	useGetAllOrderQuoteQuery,
	useGetAllOrdersQuery,
	useGetOrganizationQuery,
	OrderTypes,
	useDeleteOrganizationAddressMutation,
	useDeleteOrganizationUserMutation,
	useInviteOrganizationUserMutation,
	useSaveOrganizationLogoMutation,
	StatusEnum,
	useUpdateOrganizationAddressMutation,
} from '@codezo/graphql';
import {
	ClientBrands,
	ClientCallout,
	ClientContacts,
	ClientDetails,
	ClientHero,
	ClientListSection,
	ClientOverview,
	ClientSections,
	EmptyState,
	SectionHead,
	Badge,
	type ClientAddress,
	type ClientBrandCard,
	type ClientFact,
	type ClientLayoutSection,
	type ClientListRow,
	type ClientMetric,
} from '@codezo/client-detail';
import {
	ArrowLeft,
	ArrowUpRight,
	Camera,
	FileText,
	Loader2,
	MoreHorizontal,
	Pencil,
	Plus,
	Send,
	Star,
	Trash2,
	Truck,
	Users,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { ImageCropModal, type ImageCropResult } from '../../../components/ui/image-crop-modal';
import { InitialsAvatar } from '../../../components/ui/initials-avatar';
import { ProductCarousel } from '../../../components/products/ProductCarousel';
import { ClientViewedCell } from '../../../components/quotes/ClientViewedCell';
import { EditContactModal, type ContactData } from '../../contacts/EditContactModal';
import { AddContactModal } from '../AddContactModal';
import { AddressModal } from '../AddressModal';
import { CreateBrandModal } from '../CreateBrandModal';
import { ADDRESS_TYPE_LABEL } from '../address-utils';
import { EditCompanyDetailsModal } from './EditCompanyDetailsModal';
import { ClientSettingsSection } from './ClientSettingsSection';
import { useSetPageMeta } from '../../../hooks/useSetPageMeta';
import { formatCalendarDate, formatDate, formatMonthYear, timeAgo } from '../../../lib/utils';
import { clientContactStatus, ORDER_QUOTE_STATUS, ORDER_STATUS } from '../../../lib/status-configs';
import { useCurrencyStore } from '../../../stores/currency';

/**
 * The client page, v2 — the account as one document with an index rail.
 *
 * The default at `clients/:id`, the same way order and quote v2 took over their
 * routes. The page it replaced is still reachable at `clients/:id/v1`.
 *
 * ── What this page owns, and what the package owns ──
 *
 * Everything here is data and permission: five queries, the mapping onto the
 * package's shapes, and every button. The package owns the arrangement and has
 * no idea an admin is looking at it — which is what will let the org app render
 * the same sections for a client looking at their own account.
 *
 * ── The money ──
 *
 * Totals are formatted HERE, against the global currency selector, because the
 * package takes pre-formatted strings. Orders carry `totalAmount` in the
 * requested currency; quotes carry no total at all on the list query, so their
 * figure column stays empty rather than showing a zero that means "unknown".
 */

/** Quotes and orders show the most recent handful with a link to the full
 * list, filtered to this client. Products scroll through all of them. */
const RECENT_LIMIT = 5;

/** Drops the blanks and puts a separator between what is left — the string
 * version of this could not carry a mono span for the registration number. */
function joinMeta(parts: ReactNode[]): ReactNode {
	const shown = parts.filter(Boolean);
	return shown.map((part, i) => (
		<span key={i}>
			{i > 0 && ' · '}
			{part}
		</span>
	));
}

/** The address shape `AddressModal` edits — taken from the query rather than
 * redeclared, so a field added to one follows into the other. */
type AddressRecord = NonNullable<
	NonNullable<ReturnType<typeof useGetOrganizationQuery>['data']>['getOrganization']
>['addresses'] extends (infer T)[] | null | undefined
	? T
	: never;

export function ClientDetailPageV2() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { selectedCurrency } = useCurrencyStore();
	const revealRef = useRef<((sectionId: string) => void) | null>(null);

	/* Logo upload — the same flow as v1, deliberately: two blobs (the original
	   so the crop stays re-editable, and the cropped one that is displayed),
	   PUT straight to Azure, then persisted together. */
	const [editingDetails, setEditingDetails] = useState(false);
	const [addressModalOpen, setAddressModalOpen] = useState(false);
	const [deleteAddressTarget, setDeleteAddressTarget] = useState<AddressRecord | null>(null);
	const [addContactOpen, setAddContactOpen] = useState(false);
	const [deleteContactTarget, setDeleteContactTarget] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [deleteOrganizationUser, { loading: deletingContact }] =
		useDeleteOrganizationUserMutation();
	const [editContact, setEditContact] = useState<ContactData | null>(null);
	const [inviteTarget, setInviteTarget] = useState<{
		id: string;
		name: string;
		invitedAt?: string | null;
	} | null>(null);
	const [inviteOrganizationUser, { loading: inviting }] = useInviteOrganizationUserMutation();

	const handleInvite = async () => {
		if (!inviteTarget) return;
		try {
			const { data } = await inviteOrganizationUser({
				variables: { input: { id: inviteTarget.id } },
			});
			if (data?.inviteOrganizationUser?.success) {
				toast.success(inviteTarget.invitedAt ? 'Invitation re-sent' : 'Invitation sent');
				refetchOrg();
			} else {
				toast.error(data?.inviteOrganizationUser?.message || 'Failed to send invitation');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to send invitation');
		}
		setInviteTarget(null);
	};
	const [updateAddress] = useUpdateOrganizationAddressMutation();
	const [deleteAddress, { loading: deletingAddress }] = useDeleteOrganizationAddressMutation();
	const [editingAddress, setEditingAddress] = useState<AddressRecord | null>(null);

	const [logoCropOpen, setLogoCropOpen] = useState(false);
	const [croppedLogoUrl, setCroppedLogoUrl] = useState<string | null>(null);
	const [logoUploading, setLogoUploading] = useState(false);
	const [generateUploadUrl] = useGenerateOrganizationLogoUploadUrlMutation();
	const [saveLogo] = useSaveOrganizationLogoMutation();

	const {
		data: orgData,
		loading: orgLoading,
		refetch: refetchOrg,
	} = useGetOrganizationQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});
	const org = orgData?.getOrganization;

	const handleLogoSave = useCallback(
		async ({ croppedBlob, previewUrl, originalFile }: ImageCropResult) => {
			if (!id) return;

			/* Show the crop immediately — the uploads below take seconds and the
			   tile going blank in the meantime reads as a failure. */
			setCroppedLogoUrl(previewUrl);
			setLogoUploading(true);

			try {
				const [originalUrlRes, croppedUrlRes] = await Promise.all([
					generateUploadUrl({
						variables: {
							input: {
								organizationId: id,
								fileName: originalFile.name,
								fileSize: originalFile.size,
								fileType: originalFile.type,
							},
						},
					}),
					generateUploadUrl({
						variables: {
							input: {
								organizationId: id,
								fileName: `cropped-${originalFile.name.replace(/\.[^.]+$/, '')}.png`,
								fileSize: croppedBlob.size,
								fileType: 'image/png',
							},
						},
					}),
				]);

				const originalUploadUrl =
					originalUrlRes.data?.generateOrganizationLogoUploadUrl?.uploadUrl;
				const originalBlobUrl =
					originalUrlRes.data?.generateOrganizationLogoUploadUrl?.blobUrl;
				const croppedUploadUrl =
					croppedUrlRes.data?.generateOrganizationLogoUploadUrl?.uploadUrl;
				const croppedBlobUrl =
					croppedUrlRes.data?.generateOrganizationLogoUploadUrl?.blobUrl;

				if (
					!originalUploadUrl ||
					!originalBlobUrl ||
					!croppedUploadUrl ||
					!croppedBlobUrl
				) {
					throw new Error('Failed to generate upload URLs');
				}

				await Promise.all([
					fetch(originalUploadUrl, {
						method: 'PUT',
						headers: {
							'x-ms-blob-type': 'BlockBlob',
							'Content-Type': originalFile.type,
						},
						body: originalFile,
					}),
					fetch(croppedUploadUrl, {
						method: 'PUT',
						headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/png' },
						body: croppedBlob,
					}),
				]);

				await saveLogo({
					variables: {
						input: {
							organizationId: id,
							originalLogo: originalBlobUrl,
							logo: croppedBlobUrl,
						},
					},
				});
			} catch {
				toast.error('Failed to upload logo');
				setCroppedLogoUrl(null);
			} finally {
				setLogoUploading(false);
			}
		},
		[id, generateUploadUrl, saveLogo]
	);

	const { data: brandData, refetch: refetchBrands } = useGetAllBrandsQuery({
		variables: { input: { organizationId: id!, pagination: { limit: 50, currentPage: 1 } } },
		fetchPolicy: 'no-cache',
		skip: !id,
	});
	const brands = useMemo(() => brandData?.getAllBrand?.data ?? [], [brandData]);

	/* Clients with seats keep their own kits, but the admin can still add and
	   remove them — v1's brand tab could, and v2 dropped it by accident. */
	const [createBrandOpen, setCreateBrandOpen] = useState(false);
	const [deleteBrandTarget, setDeleteBrandTarget] = useState<{ id: string; name: string } | null>(
		null
	);
	const [deleteBrand, { loading: deletingBrand }] = useDeleteBrandMutation();

	const handleDeleteBrand = async () => {
		if (!deleteBrandTarget) return;
		try {
			await deleteBrand({ variables: { input: { id: deleteBrandTarget.id } } });
			toast.success('Brand deleted');
			setDeleteBrandTarget(null);
			refetchBrands();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete brand');
		}
	};

	const { data: quoteData } = useGetAllOrderQuoteQuery({
		variables: {
			input: {
				organizationIds: id ? [id] : undefined,
				pagination: { limit: RECENT_LIMIT, currentPage: 1 },
				/* Explicit, and matching the overview lists' default. Without it
				   the server's own ordering decides which five you see, which is
				   not necessarily the newest five. */
				order: { field: 'createdAt', direction: OrderTypes.Desc },
			},
		},
		fetchPolicy: 'no-cache',
		skip: !id,
	});
	const quotes = quoteData?.getAllOrderQuote?.data ?? [];
	const quoteTotal = quoteData?.getAllOrderQuote?.pageInfo?.totalCount ?? quotes.length;

	const { data: orderData } = useGetAllOrdersQuery({
		variables: {
			input: {
				organizationIds: id ? [id] : undefined,
				pagination: { limit: RECENT_LIMIT, currentPage: 1 },
				order: { field: 'createdAt', direction: OrderTypes.Desc },
			},
			inCurrency: selectedCurrency,
		},
		fetchPolicy: 'no-cache',
		skip: !id,
	});
	const orders = orderData?.getAllOrders?.data ?? [];
	const orderTotal = orderData?.getAllOrders?.pageInfo?.totalCount ?? orders.length;

	/* The rail and overview count, reported by the Products section's carousel
	   rather than queried a second time. */
	const [productTotal, setProductTotal] = useState(0);

	useSetPageMeta(org?.name ?? 'Client', [
		{ label: 'Clients', path: '/clients' },
		{ label: org?.name ?? 'Client' },
	]);

	const money = useMemo(
		() =>
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: selectedCurrency,
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}),
		[selectedCurrency]
	);

	/* ── brands → cards ── */
	const brandCards: ClientBrandCard[] = useMemo(
		() =>
			brands.map((brand) => {
				const palette = brand.colorPalette;
				const colors = palette
					? [
							...(palette.primary ?? []),
							...(palette.secondary ?? []),
							...(palette.accent ?? []),
							...(palette.neutral ?? []),
						].map((c) => c.hex)
					: [];
				const fonts = [
					brand.typography?.primaryFont,
					brand.typography?.secondaryFont,
				].filter((f): f is string => Boolean(f));
				return {
					id: brand.id,
					name: brand.name,
					code: brand.name.slice(0, 2).toUpperCase(),
					kind: brand.description ?? null,
					colors,
					fonts,
					fileCount: brand.brandFiles?.length ?? 0,
					updatedLabel: brand.updatedAt ? formatMonthYear(brand.updatedAt) : null,
					status: brand.brandFiles?.some((f) => f.type === BrandFileType.Logo)
						? null
						: { label: 'No logo', tone: 'warn' as const },
				};
			}),
		[brands]
	);

	/* The hero has no palette of its own — it borrows the account's brands', one
	   named column each. Capped at six so the names stay readable; past that the
	   Brands section is the place to see the rest. */
	const heroBrands = useMemo(
		() =>
			brandCards
				.slice(0, 6)
				.map((b) => ({ name: b.name, colors: (b.colors ?? []).slice(0, 4) })),
		[brandCards]
	);

	/* ── lists → rows ── */
	const quoteRows: ClientListRow[] = quotes.map((quote) => ({
		id: quote.id,
		title: quote.name ?? 'Untitled quote',
		subtitle: quote.purpose ?? null,
		cells: {
			status: quote.status ? (
				<Badge tone={ORDER_QUOTE_STATUS[quote.status]?.tone ?? 'neutral'} dot>
					{ORDER_QUOTE_STATUS[quote.status]?.label ?? quote.status}
				</Badge>
			) : null,
			viewed: (
				<ClientViewedCell
					lastClientViewedAt={quote.lastClientViewedAt}
					clientViewCount={quote.clientViewCount}
				/>
			),
			deadline: quote.deadline ? formatDate(quote.deadline) : null,
			created: quote.createdAt ? formatDate(quote.createdAt) : null,
		},
	}));

	const orderRows: ClientListRow[] = orders.map((order) => ({
		id: order.id,
		title: order.name ?? 'Untitled order',
		subtitle: order.orderNumber ? `#${order.orderNumber}` : null,
		cells: {
			amount: order.totalAmount != null ? money.format(order.totalAmount) : null,
			status: order.status ? (
				<Badge tone={ORDER_STATUS[order.status]?.tone ?? 'neutral'} dot>
					{ORDER_STATUS[order.status]?.label ?? order.status}
				</Badge>
			) : null,
			created: order.createdAt ? formatDate(order.createdAt) : null,
		},
	}));

	/* ── company facts ──
	   Read-only cells; everything editable lives in the form behind
	   "Edit details", which is v1's section and owns its own mutation. */
	/* The company number, not the VAT one where both exist: VAT is derived from
	   it in most countries, and the registration is what a client quotes back. */
	const registrations = org?.businessRegistrations ?? [];
	const primaryRegistration = registrations.find((r) => r.type !== 'VAT') ?? registrations[0];

	const facts: ClientFact[] = org
		? [
				{ label: 'Legal name', value: org.name },
				...(org.businessRegistrations ?? []).map((reg) => ({
					label: reg.type,
					value: reg.number,
					mono: true,
				})),
				{ label: 'Website', value: org.websiteUrl },
				{ label: 'Industry', value: org.organizationIndustry?.name },
				{ label: 'Country', value: org.countryCode },
				{ label: 'Currency', value: org.organizationSetting?.currency, mono: true },
			]
		: [];

	/* Primary first — the default for a quote should be the one you read first,
	   the same order v1's address section uses. */
	const addressRecords = [...(org?.addresses ?? [])].sort(
		(a, b) => Number(b.isPrimary) - Number(a.isPrimary)
	);
	const addressCards: ClientAddress[] = addressRecords.map((address) => ({
		id: address.id,
		kind: ADDRESS_TYPE_LABEL[address.addressType],
		label: address.name,
		lines: [
			[address.addressLine1, address.addressLine2].filter(Boolean).join(', '),
			[address.postalCode, address.city].filter(Boolean).join(' '),
			[address.stateCode, address.countryCode].filter(Boolean).join(' · '),
		].filter(Boolean),
		isPrimary: address.isPrimary ?? false,
		/* Billing reads stronger than the rest — "where does the invoice go" is
		   what someone scans this grid for. */
		emphasis: address.addressType === AddressType.Billing,
	}));

	/**
	 * Promotion goes through the same update mutation — `isPrimary` is a field on
	 * the address, so the whole address is sent back unchanged apart from it.
	 */
	const handleSetPrimaryAddress = async (address: AddressRecord) => {
		try {
			await updateAddress({
				variables: {
					id: address.id,
					input: {
						addressLine1: address.addressLine1,
						addressLine2: address.addressLine2 ?? undefined,
						city: address.city,
						stateCode: address.stateCode ?? undefined,
						postalCode: address.postalCode,
						countryCode: address.countryCode,
						googlePlaceId: address.googlePlaceId ?? undefined,
						latitude: address.latitude ?? undefined,
						longitude: address.longitude ?? undefined,
						name: address.name ?? undefined,
						addressType: address.addressType,
						isPrimary: true,
					},
				},
			});
			toast.success('Primary address updated');
			refetchOrg();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update primary address');
		}
	};

	const handleDeleteAddress = async () => {
		if (!deleteAddressTarget) return;
		try {
			await deleteAddress({ variables: { id: deleteAddressTarget.id } });
			toast.success('Address removed');
			setDeleteAddressTarget(null);
			refetchOrg();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove address');
		}
	};

	const handleDeleteContact = async () => {
		if (!deleteContactTarget) return;
		try {
			await deleteOrganizationUser({ variables: { input: { id: deleteContactTarget.id } } });
			toast.success('Contact removed');
			setDeleteContactTarget(null);
			refetchOrg();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove contact');
		}
	};

	/* ── contacts ── */
	/* `OrganizationUser` carries no role and no email of its own — the address
	   belongs to the linked authentication user, and the job position is the
	   closest thing to a role the record has. */
	const userRecords = org?.organizationUsers ?? [];
	const contacts = userRecords.map((user) => {
		const email = user.authenticationUser?.email ?? null;
		/* The real state, from the same helper the contacts table uses — it reads
		   status, isGuest and invitedAt together, which is the only way to tell
		   "never asked" from "waiting on them" from "active". */
		const status = clientContactStatus(user.status, user.isGuest, user.invitedAt);
		return {
			id: user.id,
			name: [user.firstName, user.lastName].filter(Boolean).join(' ') || email || 'Unnamed',
			role: user.jobPosition ?? null,
			email,
			phone: user.phone,
			birthdayLabel: formatCalendarDate(user.birthday),
			joinedLabel: user.createdAt ? formatDate(user.createdAt) : null,
			status: { label: status.label, tone: status.tone },
		};
	});

	const activeContacts = contacts.filter((c) => c.status.tone === 'success').length;

	const metrics: ClientMetric[] = [
		{ label: 'Brands', value: brandCards.length, sectionId: 'brands' },
		{ label: 'Products', value: productTotal, sectionId: 'products' },
		{ label: 'Quotes', value: quoteTotal, sectionId: 'quotes' },
		{ label: 'Orders', value: orderTotal, sectionId: 'orders' },
		{ label: 'Contacts', value: contacts.length, sectionId: 'contacts' },
	];

	if (orgLoading) {
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<div className="flex min-h-[50vh] items-center justify-center">
					<Loader2 className="animate-spin text-text-4" size={20} />
				</div>
			</div>
		);
	}

	if (!org) {
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<div className="py-16 text-center text-sm text-text-3">
					This client could not be found.
				</div>
			</div>
		);
	}

	const sections: ClientLayoutSection[] = [
		{
			id: 'overview',
			label: 'Overview',
			content: (
				<ClientOverview
					metrics={metrics}
					onMetricClick={(sectionId) => revealRef.current?.(sectionId)}
					callout={
						<ClientCallout
							icon={<Users size={15} />}
							title={
								contacts.length > 0
									? 'Client workspace active'
									: 'No client seats yet'
							}
							body={
								contacts.length > 0
									? `${contacts.length} seat${contacts.length === 1 ? '' : 's'} · they can edit their brand kits and review offers in their own view`
									: 'Invite a contact to give this client access to their own workspace.'
							}
						/>
					}
				/>
			),
		},
		{
			id: 'details',
			label: 'Company & addresses',
			content: (
				<ClientDetails
					facts={facts}
					addresses={addressCards}
					action={
						<Button variant="outline" size="sm" onClick={() => setEditingDetails(true)}>
							Edit details
						</Button>
					}
					renderAddressIcon={(address) =>
						address.kind === ADDRESS_TYPE_LABEL[AddressType.Shipping] ? (
							<Truck size={13} />
						) : (
							<FileText size={13} />
						)
					}
					renderAddressAction={(address) => {
						const record = addressRecords.find((a) => a.id === address.id);
						if (!record) return null;
						/* The primary address is what quotes and orders default to;
						   removing it silently would leave them without one. */
						const blockedFromDelete =
							Boolean(record.isPrimary) && addressRecords.length > 1;
						return (
							<DropdownMenu>
								<DropdownMenuTrigger
									aria-label="Address actions"
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-2)] text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<MoreHorizontal size={13} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										onClick={() => {
											setEditingAddress(record);
											setAddressModalOpen(true);
										}}
									>
										<Pencil size={14} /> Edit
									</DropdownMenuItem>
									{!record.isPrimary && (
										<DropdownMenuItem
											onClick={() => handleSetPrimaryAddress(record)}
										>
											<Star size={14} /> Set as primary
										</DropdownMenuItem>
									)}
									<DropdownMenuSeparator />
									<DropdownMenuItem
										variant="destructive"
										disabled={blockedFromDelete}
										onClick={() => setDeleteAddressTarget(record)}
									>
										<Trash2 size={14} />{' '}
										{blockedFromDelete
											? 'Set another as primary first'
											: 'Remove'}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						);
					}}
					addAddressSlot={
						<button
							type="button"
							onClick={() => {
								setEditingAddress(null);
								setAddressModalOpen(true);
							}}
							className="flex min-h-[9.5rem] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[var(--r-3)] border border-dashed border-line-2 bg-transparent text-xs text-text-3 transition-colors hover:border-line-3 hover:text-text-1"
						>
							<Plus size={14} />
							Add address
						</button>
					}
				/>
			),
		},
		{
			id: 'brands',
			label: 'Brands',
			count: brandCards.length,
			content: (
				<ClientBrands
					brands={brandCards}
					meta={
						brandCards.length > 0
							? `${brandCards.length} kit${brandCards.length === 1 ? '' : 's'}`
							: undefined
					}
					action={
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCreateBrandOpen(true)}
						>
							<Plus size={13} data-icon="inline-start" />
							New brand
						</Button>
					}
					emptyAction={
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCreateBrandOpen(true)}
						>
							<Plus size={13} data-icon="inline-start" />
							New brand
						</Button>
					}
					brandHref={(brand) => `/clients/${org.id}/brands/${brand.id}`}
					renderBrandAction={(brand) => (
						<>
							<DropdownMenu>
								<DropdownMenuTrigger
									aria-label={`Actions for ${brand.name}`}
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-2)] text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<MoreHorizontal size={13} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										variant="destructive"
										onClick={() =>
											setDeleteBrandTarget({ id: brand.id, name: brand.name })
										}
									>
										<Trash2 size={14} /> Delete
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate(`/clients/${org.id}/brands/${brand.id}`)}
							>
								<ArrowUpRight size={12} data-icon="inline-start" />
								Open
							</Button>
						</>
					)}
				/>
			),
		},
		{
			id: 'products',
			label: 'Products',
			count: productTotal,
			content: (
				<ProductCarousel
					filter={{ organizationId: org.id }}
					onTotal={setProductTotal}
					head={(total) => (
						<SectionHead
							title="Products"
							meta={total > 0 ? `${total} total` : undefined}
							action={
								total > 0 ? (
									<Button
										variant="outline"
										size="sm"
										onClick={() => navigate(`/products?client=${org.id}`)}
									>
										See all
										<ArrowUpRight size={14} data-icon="inline-end" />
									</Button>
								) : undefined
							}
						/>
					)}
					empty={<EmptyState title="No products yet" />}
				/>
			),
		},
		{
			id: 'quotes',
			label: 'Quotes',
			count: quoteTotal,
			content: (
				<ClientListSection
					title="Quotes"
					identityLabel="Quote"
					meta={
						quoteTotal > quoteRows.length
							? `${quoteRows.length} most recent of ${quoteTotal}`
							: `${quoteTotal} total`
					}
					action={
						quoteTotal > quoteRows.length ? (
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate(`/quotes?clients=${org.id}`)}
							>
								See all
								<ArrowUpRight size={14} data-icon="inline-end" />
							</Button>
						) : undefined
					}
					columns={[
						{ key: 'status', label: 'Status' },
						{ key: 'viewed', label: 'Client viewed', collapse: true },
						{ key: 'deadline', label: 'Deadline', align: 'right' },
						{ key: 'created', label: 'Created', align: 'right', collapse: true },
					]}
					rows={quoteRows}
					icon={<FileText size={13} />}
					emptyTitle="No quotes yet"
					rowHref={(row) => `/quotes/${row.id}`}
					onRowClick={(row) => navigate(`/quotes/${row.id}`)}
				/>
			),
		},
		{
			id: 'orders',
			label: 'Orders',
			count: orderTotal,
			content: (
				<ClientListSection
					title="Orders"
					identityLabel="Order"
					meta={
						orderTotal > orderRows.length
							? `${orderRows.length} most recent of ${orderTotal}`
							: `${orderTotal} total`
					}
					action={
						orderTotal > orderRows.length ? (
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate(`/orders?clients=${org.id}`)}
							>
								See all
								<ArrowUpRight size={14} data-icon="inline-end" />
							</Button>
						) : undefined
					}
					columns={[
						{ key: 'amount', label: 'Amount', align: 'right' },
						{ key: 'status', label: 'Status' },
						{ key: 'created', label: 'Created', align: 'right', collapse: true },
					]}
					rows={orderRows}
					icon={<Truck size={13} />}
					emptyTitle="No orders yet"
					rowHref={(row) => `/orders/${row.id}`}
					onRowClick={(row) => navigate(`/orders/${row.id}`)}
				/>
			),
		},
		{
			id: 'contacts',
			label: 'Contacts & access',
			count: contacts.length,
			content: (
				<ClientContacts
					contacts={contacts}
					meta={
						contacts.length > 0
							? `${activeContacts} of ${contacts.length} active`
							: undefined
					}
					action={
						<Button variant="outline" size="sm" onClick={() => setAddContactOpen(true)}>
							<Plus size={13} data-icon="inline-start" />
							Invite contact
						</Button>
					}
					emptyAction={
						<Button variant="outline" size="sm" onClick={() => setAddContactOpen(true)}>
							<Plus size={13} data-icon="inline-start" />
							Invite contact
						</Button>
					}
					renderContactAction={(contact) => {
						const record = userRecords.find((u) => u.id === contact.id);
						return (
							<DropdownMenu>
								<DropdownMenuTrigger
									aria-label={`Actions for ${contact.name}`}
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-2)] text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<MoreHorizontal size={13} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									{/* Disabled rather than hidden for an active contact, so
									    the action stays discoverable and its unavailability
									    is explained rather than mysterious — v1's reasoning. */}
									<DropdownMenuItem
										disabled={inviting || record?.status !== StatusEnum.Pending}
										onClick={() =>
											setInviteTarget({
												id: contact.id,
												name: contact.name,
												invitedAt: record?.invitedAt,
											})
										}
									>
										<Send size={14} />
										{record?.invitedAt
											? 'Re-send invitation'
											: 'Send invitation'}
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() =>
											record &&
											setEditContact({
												id: record.id,
												firstName: record.firstName,
												lastName: record.lastName,
												email: record.authenticationUser?.email,
												jobPosition: record.jobPosition,
												phone: record.phone,
												birthday: record.birthday,
												notes: record.notes,
												organizationName: org.name,
											})
										}
									>
										<Pencil size={14} /> Edit
									</DropdownMenuItem>
									<DropdownMenuItem
										variant="destructive"
										onClick={() =>
											setDeleteContactTarget({
												id: contact.id,
												name: contact.name,
											})
										}
									>
										<Trash2 size={14} /> Remove
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						);
					}}
				/>
			),
		},
		{
			id: 'settings',
			label: 'Settings',
			content: <ClientSettingsSection organizationId={org.id} />,
		},
	];

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<button
				onClick={() => navigate('/clients')}
				className="mb-5 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				All clients
			</button>

			<EditCompanyDetailsModal
				open={editingDetails}
				onOpenChange={setEditingDetails}
				organization={org}
				onSaved={() => {
					refetchOrg();
				}}
			/>

			<CreateBrandModal
				open={createBrandOpen}
				onOpenChange={setCreateBrandOpen}
				organizationId={org.id}
				onSuccess={() => {
					refetchBrands();
				}}
			/>

			<ConfirmDialog
				open={!!deleteBrandTarget}
				onOpenChange={(v) => !v && setDeleteBrandTarget(null)}
				title="Delete brand"
				description={`Are you sure you want to delete "${deleteBrandTarget?.name}"? This action cannot be undone.`}
				confirmLabel="Delete"
				loading={deletingBrand}
				onConfirm={handleDeleteBrand}
			/>

			<AddContactModal
				open={addContactOpen}
				onOpenChange={setAddContactOpen}
				organizationId={org.id}
				onSuccess={() => {
					refetchOrg();
				}}
			/>

			<EditContactModal
				open={!!editContact}
				onOpenChange={(v) => !v && setEditContact(null)}
				contact={editContact}
				onSuccess={() => {
					refetchOrg();
					setEditContact(null);
				}}
			/>

			{/* Re-sending is worth confirming: it emails a real person again, and
			    the earlier link staying valid is the part they will ask about. */}
			<ConfirmDialog
				open={!!inviteTarget}
				onOpenChange={(v) => !v && setInviteTarget(null)}
				variant="default"
				title={inviteTarget?.invitedAt ? 'Re-send invitation' : 'Send invitation'}
				description={
					inviteTarget?.invitedAt
						? `${inviteTarget.name} was already emailed an invitation ${timeAgo(inviteTarget.invitedAt)}. Sending another emails them again, and the earlier invitation link stays valid.`
						: `${inviteTarget?.name} will be emailed an invitation to set up their account.`
				}
				confirmLabel={inviteTarget?.invitedAt ? 'Re-send' : 'Send invitation'}
				loading={inviting}
				onConfirm={handleInvite}
			/>

			<ConfirmDialog
				open={!!deleteContactTarget}
				onOpenChange={(v) => !v && setDeleteContactTarget(null)}
				title="Remove contact"
				description={`${deleteContactTarget?.name} will lose access to this organization.`}
				confirmLabel="Remove"
				loading={deletingContact}
				onConfirm={handleDeleteContact}
			/>

			<ConfirmDialog
				open={!!deleteAddressTarget}
				onOpenChange={(v) => !v && setDeleteAddressTarget(null)}
				title="Remove address"
				description={`${deleteAddressTarget?.name || deleteAddressTarget?.addressLine1} will no longer be selectable on quotes and orders.`}
				confirmLabel="Remove"
				loading={deletingAddress}
				onConfirm={handleDeleteAddress}
			/>

			<AddressModal
				open={addressModalOpen}
				onOpenChange={setAddressModalOpen}
				organizationId={org.id}
				address={editingAddress}
				isFirstAddress={addressRecords.length === 0}
				onSuccess={() => {
					refetchOrg();
					setAddressModalOpen(false);
					setEditingAddress(null);
				}}
			/>

			<ImageCropModal
				open={logoCropOpen}
				onOpenChange={setLogoCropOpen}
				title="Upload logo"
				cropTitle="Crop logo"
				saveLabel="Save logo"
				initialImage={org.originalLogo}
				onSave={handleLogoSave}
			/>

			<ClientHero
				name={org.name}
				code={org.name.slice(0, 2).toUpperCase()}
				/* The hero owns the tile's size and offset; the upload flow is
				   ours, so the mark itself is passed in. */
				mark={
					<button
						type="button"
						onClick={() => setLogoCropOpen(true)}
						disabled={logoUploading}
						aria-label="Change logo"
						className="group relative block shrink-0 cursor-pointer rounded-[var(--r-4)]"
					>
						{croppedLogoUrl || org.logo ? (
							<img
								src={(croppedLogoUrl || org.logo) as string}
								alt={org.name}
								className="h-[76px] w-[76px] rounded-[var(--r-4)] border border-line-1 object-cover"
							/>
						) : (
							<InitialsAvatar
								name={org.name}
								size={76}
								shape="rounded"
								maxInitials={3}
							/>
						)}
						<span className="absolute inset-0 flex items-center justify-center rounded-[var(--r-4)] bg-black/0 text-white/0 transition-all group-hover:bg-black/50 group-hover:text-white/90">
							{logoUploading ? (
								<Loader2 size={20} className="animate-spin" />
							) : (
								<Camera size={20} />
							)}
						</span>
					</button>
				}
				brands={heroBrands}
				/* The registration is the one identifier people quote back to us, so
				   it rides in the subline rather than only in the facts grid below.
				   Nodes, not a joined string: the number wants mono. */
				meta={joinMeta([
					org.organizationIndustry?.name,
					org.countryCode,
					primaryRegistration && (
						<>
							{primaryRegistration.type}{' '}
							<span className="font-mono">{primaryRegistration.number}</span>
						</>
					),
					org.createdAt ? `client since ${formatMonthYear(org.createdAt)}` : null,
				])}
			/>

			<ClientSections sections={sections} revealRef={revealRef} />
		</div>
	);
}
